import { useEffect, useState } from "react";
import { GetStaticPropsContext, NextPage } from "next";
import { useRouter } from "next/router";
import { useUserProfile } from "@providers/profile";
import { events } from "@providers/analytics/analytics";
import { Sanity } from "@models/sanity.model";
import { getGlobals } from "@models/globals.model";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@utils/constants";
import { isBlank } from "@utils/index";
import { invalidField } from "@utils/validations";
import Layout from "@components/layout";
import Loading from "@components/ui/loading";

const validateRange = (value: string, min: number, max: number) =>
  value.length <= max && value.length >= min;

const ResetPassword: NextPage<Sanity.PageProps> = ({ globals }) => {
  const { query, push } = useRouter();
  const {
    profileState: { authState },
    resetPassword,
  } = useUserProfile();
  const code = query.code as string;
  const email = query.email as string;
  const [state, setState] = useState({
    loading: false,
    password: "",
    password_confirmation: "",
    submitted: false,
    error: "",
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // loading set to true before making the request
    setState({ ...state, loading: true });

    try {
      await resetPassword(code, state.password, email);
      // triggers a re-render of the component
      setState({
        ...state,
        submitted: true,
      });
      // Redirect home after a few seconds
      setTimeout(() => push("/"), 2000);
    } catch (e) {
      const error = e.response.body;
      setState({
        ...state,
        error: error ? error.message : "An unknown error occurred",
        loading: false,
      });
    } finally {
      // events is from segment
      events.password.change(authState.value, email);
    }
  };

  const invalidPasswordMsg = invalidField("Password", PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH);
  // TODO This should be using the Field construct and our built-in validators, but
  // the validators need to be more flexible
  // TODO Update the validator to be able to check that two fields match
  const passwordMismatch =
    validateRange(state.password, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH) &&
    validateRange(state.password_confirmation, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH) &&
    state.password !== state.password_confirmation;

  return (
    <Layout globals={globals} hideBanner={false} seo={{ title: "Reset Password" }}>
      <div id="reset-password" className="content-page scene">
        <div className="grid-container">
          <h1 className="h3">Update password</h1>
          <section>
            <div className="reset-pswd-form">
              <form onSubmit={onSubmit}>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email ?? ""}
                  disabled={true}
                />
                <input
                  className="password"
                  type="password"
                  placeholder="New password"
                  autoComplete="new-password"
                  value={state.password}
                  required
                  onChange={(e) => setState({ ...state, password: e.target.value })}
                />
                {!isBlank(state.password) &&
                  !validateRange(state.password, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH) && (
                    <span className="error">{invalidPasswordMsg}</span>
                  )}
                <input
                  className="password"
                  type="password"
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  value={state.password_confirmation}
                  required
                  onChange={(e) => setState({ ...state, password_confirmation: e.target.value })}
                />
                {!isBlank(state.password_confirmation) &&
                  !validateRange(
                    state.password_confirmation,
                    PASSWORD_MIN_LENGTH,
                    PASSWORD_MAX_LENGTH
                  ) && <span className="error">{invalidPasswordMsg}</span>}
                {passwordMismatch && <div className="error">Passwords do not match</div>}
                {state.error && <div className="error">{state.error}</div>}
                {state.submitted && !state.error && (
                  <div className="confirmation">Password updated</div>
                )}
                <button
                  className="button"
                  type="submit"
                  disabled={
                    !validateRange(state.password, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH) ||
                    state.password !== state.password_confirmation ||
                    state.loading ||
                    (state.submitted && state.error === "")
                  }>
                  Update Password
                </button>
              </form>
            </div>
          </section>
        </div>
        <Loading visible={state.loading} />
      </div>
    </Layout>
  );
};

export default ResetPassword;

export async function getStaticProps() {
  const [globals] = await Promise.all([await getGlobals()]);

  return {
    props: {
      globals,
    },
  };
}
